import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import { APP_CONFIG } from '../constants/appConfig';

export function useSessionTimeout() {
  const navigate               = useNavigate();
  const { jwtToken, resetSession } = useSession();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const warningTimerId  = useRef<ReturnType<typeof setTimeout>>();
  const expireTimerId   = useRef<ReturnType<typeof setTimeout>>();
  const countdownId     = useRef<ReturnType<typeof setInterval>>();
  const warningShowing  = useRef(false);

  // Stable refs so timer closures always call the latest versions
  const resetSessionRef = useRef(resetSession);
  const navigateRef     = useRef(navigate);
  useEffect(() => { resetSessionRef.current = resetSession; }, [resetSession]);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);

  const clearAll = useCallback(() => {
    clearTimeout(warningTimerId.current);
    clearTimeout(expireTimerId.current);
    clearInterval(countdownId.current);
  }, []);

  const startTimers = useCallback(() => {
    clearAll();
    warningShowing.current = false;
    setShowWarning(false);

    const warningDelay = APP_CONFIG.sessionTimeoutMs - APP_CONFIG.sessionWarningMs;

    warningTimerId.current = setTimeout(() => {
      warningShowing.current = true;
      setShowWarning(true);
      setSecondsLeft(Math.round(APP_CONFIG.sessionWarningMs / 1000));
      countdownId.current = setInterval(() => {
        setSecondsLeft(s => Math.max(0, s - 1));
      }, 1000);
    }, warningDelay);

    expireTimerId.current = setTimeout(() => {
      clearAll();
      warningShowing.current = false;
      setShowWarning(false);
      resetSessionRef.current();
      navigateRef.current('/error?reason=SESSION_EXPIRED', { replace: true });
    }, APP_CONFIG.sessionTimeoutMs);
  }, [clearAll]);

  // Start/stop the inactivity clock based on whether a session exists
  useEffect(() => {
    if (!jwtToken) {
      clearAll();
      warningShowing.current = false;
      setShowWarning(false);
      return;
    }
    startTimers();
    return clearAll;
  }, [jwtToken, startTimers, clearAll]);

  // Reset timers on any user activity — but not while the warning is visible
  useEffect(() => {
    if (!jwtToken) return;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    const handle = () => { if (!warningShowing.current) startTimers(); };
    events.forEach(ev => window.addEventListener(ev, handle, { passive: true }));
    return () => events.forEach(ev => window.removeEventListener(ev, handle));
  }, [jwtToken, startTimers]);

  const stayActive = useCallback(() => {
    startTimers();
  }, [startTimers]);

  return { showWarning, secondsLeft, stayActive };
}
